import { useEffect } from 'react'

import { useQuery } from '@apollo/client'
import { Button, Chip, Grid, Stack } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { useLocation } from 'react-router-dom'

import { notBatchedFilter } from 'components/accountsPayable/AccountPayableFilter/AccountPayableQueryFilters'
import {
  ControlledCheckbox,
  ControlledDatePicker,
  ControlledRadioGroup,
  ControlledSelector,
} from 'components/common'
import XeroPurchaseCodesMultipleAutocomplete from 'components/common/custom-queries/XeroCodesMultipleAutocomplete'
import { AccountsPayableRecordCategory, AccountsPayableFilterCriteria } from 'generated/graphql'
import { GET_ACCOUNT_PAYABLE_RECORDS_COUNT } from 'graphql/queries/AccountPayableRecordQueries'
import { accountPayableFiltersFormVar } from 'graphql/reactiveVariables'
import { isNotBatchedFilterTab } from 'helpers/accountsPayable.helpers'

import MultipleSearchAutocomplete from './MultipleSearchAutocomplete'

const defaultFormValues = {
  multipleSearch: '',
  xeroPurchaseCodes: [],
  driverName: null,
  driverNumber: null,
  courierExchangeLoadId: null,
  ourReference: null,
  invoiceAmount: null,
  dueInStartDate: null,
  dueInEndDate: null,
  category: '',
  dateSelection: AccountsPayableFilterCriteria.InvoiceDate,
  notBatched: false,
}

const FilterCriteriaForm = () => {
  const methods = useForm<any>({
    defaultValues: defaultFormValues,
  })

  const {
    control,
    reset,
    formState: { errors },
    handleSubmit,
  } = methods

  const { hash } = useLocation()
  const isNotBatchedVisible = isNotBatchedFilterTab(hash)

  // Counts every unbatched record regardless of the other filters, so outstanding ones stay visible.
  const { data: notBatchedCountData, refetch: refetchNotBatchedCount } = useQuery(
    GET_ACCOUNT_PAYABLE_RECORDS_COUNT,
    {
      fetchPolicy: 'network-only',
      variables: { where: notBatchedFilter },
      skip: !isNotBatchedVisible,
    },
  )

  const notBatchedCount = notBatchedCountData?.accountPayableRecords?.totalCount ?? 0

  const onSubmit = (data: any) => {
    accountPayableFiltersFormVar(data)
    if (isNotBatchedVisible) {
      refetchNotBatchedCount()
    }
  }

  const clearAll = () => {
    reset(defaultFormValues)
    accountPayableFiltersFormVar(defaultFormValues)
  }

  useEffect(() => {
    const accountPayableFilterForm = accountPayableFiltersFormVar()
    reset(accountPayableFilterForm)
  }, [reset])

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Grid container spacing={1}>
          <Grid item container spacing={2} xs={12} md={10}>
            <Grid item container xs={12} md alignItems='center' justifyContent='flex-start' spacing={2}>
              <Grid item xs={12} md={3}>
                <MultipleSearchAutocomplete />
              </Grid>
              <Grid item xs={12} md={3}>
                <XeroPurchaseCodesMultipleAutocomplete control={control} />
              </Grid>
              <Grid item xs={12} md={3}>
                <ControlledDatePicker
                  control={control}
                  name='dueInStartDate'
                  label='Start Date'
                  error={!!errors.startDate}
                  helperText={errors.startDate?.message}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <ControlledDatePicker
                  control={control}
                  name='dueInEndDate'
                  label='End Date'
                  error={!!errors.endDate}
                  helperText={errors.endDate?.message}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <ControlledSelector
                  control={control}
                  name='category'
                  label='Payment Category'
                  emptyValue={{
                    value: '',
                    label: 'All',
                  }}
                  options={[
                    { value: AccountsPayableRecordCategory.Telleroo, label: 'Telleroo' },
                    { value: AccountsPayableRecordCategory.HedgeFlows, label: 'HedgeFlows' },
                    { value: AccountsPayableRecordCategory.SmartPay, label: 'SmartPay' },
                  ]}
                />
              </Grid>
              {isNotBatchedVisible && (
                <Grid item xs={12} md={3}>
                  <Stack direction='row' alignItems='center'>
                    <ControlledCheckbox
                      control={control}
                      name='notBatched'
                      label='Not batched'
                      defaultValue={false}
                    />
                    {notBatchedCount > 0 && (
                      <Chip
                        size='small'
                        color='warning'
                        label={notBatchedCount}
                        sx={{ ml: -1, height: 20, fontWeight: 600 }}
                      />
                    )}
                  </Stack>
                </Grid>
              )}
              <Grid
                item
                container
                xs={12}
                md={isNotBatchedVisible ? 6 : 9}
                justifyContent='flex-end'
                alignItems='center'>
              <Grid item xs={12} md='auto'>
                <ControlledRadioGroup
                  control={control}
                  name='dateSelection'
                  row
                  options={[
                    { value: AccountsPayableFilterCriteria.InvoiceDate, label: 'Invoice Date' },
                    { value: AccountsPayableFilterCriteria.PaymentDate, label: 'Payment Date' },
                  ]}
                />
              </Grid>
            </Grid>
            </Grid>
          </Grid>

          <Grid item container xs={12} md={2} spacing={2}>
            <Grid item>
              <Button variant='contained' type='submit'>
                Search
              </Button>
            </Grid>
            <Grid item>
              <Button variant='contained' color='grey' onClick={clearAll}>
                Clear All
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  )
}

export default FilterCriteriaForm
